import { useEffect, useState } from "react";
import {
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Spin,
  Alert,
  message,
  Card,
  Segmented,
} from "antd";
import {
  SyncOutlined,
  CheckCircleTwoTone,
  ClockCircleTwoTone,
  WarningTwoTone,
} from "@ant-design/icons";
import { api } from "../../dataProvider";
import { formatMoney, formatDate } from "../../utils/money";

const { Title, Text } = Typography;

interface Transaction {
  id: number;
  account_id: number;
  date: string;
  type: "income" | "expense" | "internal_transfer";
  amount: string;
  currency: string;
  counterparty_id: number | null;
  expense_category_id: number | null;
  product_id: number | null;
  status: string;
  source: string;
  external_ref: string | null;
  description: string | null;
  comment: string | null;
}

const STATUS_TAG = {
  imported_unmatched: { color: "warning", text: "Не размечено" },
  categorized: { color: "blue", text: "Размечено" },
  reconciled: { color: "success", text: "Закрыто" },
  pending_bank_confirm: { color: "default", text: "Ожидает подтверждения" },
} as const;

const TYPE_TAG = {
  income: { color: "success", text: "Приход" },
  expense: { color: "error", text: "Расход" },
  internal_transfer: { color: "default", text: "Перевод" },
} as const;

export const TransactionsPage: React.FC = () => {
  const [filter, setFilter] = useState<string>("all");
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ExpenseCategory/Product селекты для inline-категоризации добавятся в
  // следующей итерации (Phase 3a UI), когда появятся /api/v1/expense-categories
  // и /api/v1/products endpoints.

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = { limit: "200" };
      if (filter !== "all") params.status = filter;
      const tx = await api.get<Transaction[]>("/transactions/", params);
      setTransactions(tx);
    } catch (e: unknown) {
      setError((e as Error).message ?? "Ошибка");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleApplyAutoCategorization = async () => {
    try {
      const result = await api.post<{ matched: number; unmatched_left: number }>(
        "/categorization/apply",
      );
      message.success(
        `Размечено: ${result.matched}, осталось: ${result.unmatched_left}`,
      );
      reload();
    } catch (e: unknown) {
      message.error((e as Error).message ?? "Ошибка");
    }
  };

  const columns = [
    {
      title: "Дата",
      dataIndex: "date",
      key: "date",
      width: 110,
      render: (v: string) => formatDate(v),
      sorter: (a: Transaction, b: Transaction) => a.date.localeCompare(b.date),
      defaultSortOrder: "descend" as const,
    },
    {
      title: "Тип",
      dataIndex: "type",
      key: "type",
      width: 110,
      render: (v: keyof typeof TYPE_TAG) => {
        const t = TYPE_TAG[v];
        return <Tag color={t.color}>{t.text}</Tag>;
      },
    },
    {
      title: "Сумма",
      dataIndex: "amount",
      key: "amount",
      width: 140,
      align: "right" as const,
      render: (v: string, row: Transaction) => (
        <Text
          strong
          style={{
            color:
              row.type === "income"
                ? "#52c41a"
                : row.type === "expense"
                ? "#f5222d"
                : "#888",
          }}
        >
          {row.type === "expense" ? "−" : row.type === "income" ? "+" : ""}
          {formatMoney(v)}
        </Text>
      ),
    },
    {
      title: "Описание",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (v: string | null) => v || <Text type="secondary">—</Text>,
    },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
      width: 160,
      render: (v: keyof typeof STATUS_TAG) => {
        const s = STATUS_TAG[v] ?? { color: "default", text: v };
        const icon =
          v === "categorized" ? (
            <CheckCircleTwoTone twoToneColor="#1677ff" />
          ) : v === "reconciled" ? (
            <CheckCircleTwoTone twoToneColor="#52c41a" />
          ) : v === "imported_unmatched" ? (
            <WarningTwoTone twoToneColor="#fa8c16" />
          ) : (
            <ClockCircleTwoTone />
          );
        return (
          <Tag color={s.color} icon={icon}>
            {s.text}
          </Tag>
        );
      },
    },
    {
      title: "Источник",
      dataIndex: "source",
      key: "source",
      width: 130,
      render: (v: string) => <Text type="secondary">{v}</Text>,
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Space style={{ justifyContent: "space-between", width: "100%" }}>
        <Title level={3} style={{ margin: 0 }}>
          Транзакции
        </Title>
        <Space>
          <Button
            icon={<SyncOutlined />}
            onClick={handleApplyAutoCategorization}
            type="primary"
          >
            Применить авто-категоризацию
          </Button>
        </Space>
      </Space>

      <Card size="small">
        <Segmented
          value={filter}
          onChange={(v) => setFilter(String(v))}
          options={[
            { label: "Все", value: "all" },
            { label: "Не размечено", value: "imported_unmatched" },
            { label: "Размечено", value: "categorized" },
            { label: "Закрыто", value: "reconciled" },
          ]}
        />
      </Card>

      {error && <Alert type="error" message={error} />}

      <Spin spinning={loading}>
        <Table<Transaction>
          dataSource={transactions}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 50, showSizeChanger: false }}
          size="middle"
        />
      </Spin>
    </Space>
  );
};
